import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { GoogleEvent } from '../types/calendar.types';

// Sola lettura: gli eventi arrivano dal Google Calendar del proprietario tramite
// la edge function `google-calendar-events`. Niente create/update/delete.

interface RawEvent {
  id: string;
  title: string;
  description?: string | null;
  location?: string | null;
  startDate: string;
  endDate: string;
  allDay: boolean;
  htmlLink?: string | null;
  status?: string | null;
}

const rawToEvent = (r: RawEvent): GoogleEvent => ({
  id: r.id,
  title: r.title,
  description: r.description || undefined,
  location: r.location || undefined,
  startDate: new Date(r.startDate),
  endDate: new Date(r.endDate),
  allDay: r.allDay,
  htmlLink: r.htmlLink || undefined,
  status: r.status || undefined,
});

// descrizione evento a partire dai dati del lead (nome + contatti)
function leadDesc(l: { full_name?: string | null; company_name?: string | null; phone?: string | null; email?: string | null }): string {
  const name = l.full_name || l.company_name || 'Lead';
  return [name, l.phone ? `Tel: ${l.phone}` : '', l.email ? `Email: ${l.email}` : ''].filter(Boolean).join('\n');
}

function leadItem(leadId: string, title: string, start: Date, desc: string): GoogleEvent {
  return {
    id: `lead:${leadId}:${start.getTime()}`,
    title: `▶ ${title}`,
    description: desc,
    startDate: start,
    endDate: new Date(start.getTime() + 30 * 60 * 1000),
    allDay: false,
    htmlLink: `${window.location.origin}/leads/${leadId}`,
    status: 'lead',
  };
}

// Porta sul calendario le scadenze dei lead ancora aperti: le "prossime azioni"
// (leads.next_action_at) e i reminder pendenti della timeline (lead_reminders).
// Dedup per lead+orario (una prossima azione con promemoria crea entrambi).
// Non lancia mai: in errore (o RLS che blocca) torna lista vuota.
async function fetchLeadAgenda(min: Date, max: Date): Promise<GoogleEvent[]> {
  const items: GoogleEvent[] = [];
  const seen = new Set<string>();
  const minIso = min.toISOString();
  const maxIso = max.toISOString();

  // 1. Prossime azioni con data
  try {
    const { data } = await supabase
      .from('leads' as any)
      .select('id, full_name, company_name, phone, email, next_action, next_action_at, stage')
      .not('next_action_at', 'is', null)
      .gte('next_action_at', minIso)
      .lte('next_action_at', maxIso);

    (data as any[] | null || [])
      .filter((r) => r.stage !== 'vinto' && r.stage !== 'perso')
      .forEach((r) => {
        const start = new Date(r.next_action_at);
        seen.add(`${r.id}@${start.getTime()}`);
        items.push(leadItem(r.id, r.next_action || 'Azione lead', start, leadDesc(r)));
      });
  } catch { /* ignore */ }

  // 2. Reminder pendenti della timeline non già coperti da una prossima azione
  try {
    const { data } = await supabase
      .from('lead_reminders' as any)
      .select('remind_at, is_completed, event:lead_events(title, lead:leads(id, full_name, company_name, phone, email, stage))')
      .eq('is_completed', false)
      .gte('remind_at', minIso)
      .lte('remind_at', maxIso);

    (data as any[] | null || []).forEach((rm) => {
      const lead = rm.event?.lead;
      if (!lead || lead.stage === 'vinto' || lead.stage === 'perso') return;
      const start = new Date(rm.remind_at);
      const key = `${lead.id}@${start.getTime()}`;
      if (seen.has(key)) return; // già mostrato come prossima azione
      seen.add(key);
      items.push(leadItem(lead.id, rm.event?.title || 'Promemoria lead', start, leadDesc(lead)));
    });
  } catch { /* ignore */ }

  return items;
}

interface GoogleCalendarState {
  events: GoogleEvent[];
  loading: boolean;
  error: string | null;
  configured: boolean; // false → l'applicazione non è registrata su Google
  /** false → il calendario aziendale non è ancora collegato a Google. */
  collegato: boolean;
  /** Con quale account Google è collegato, quando lo è. */
  accountGoogle: string | null;
  /** true → chi sta guardando può collegare e scollegare (amministratori). */
  puoiCollegare: boolean;
  lastRange: { min: number; max: number } | null;

  fetchEvents: (timeMin: Date, timeMax: Date) => Promise<void>;
  /** Chiede a Google la pagina di autorizzazione e ci porta l'amministratore. */
  collegaGoogle: () => Promise<void>;
  scollegaGoogle: () => Promise<void>;
  leggiStatoGoogle: () => Promise<void>;
  getEventsByDate: (date: Date) => GoogleEvent[];
  getTodayEvents: () => GoogleEvent[];
  clearError: () => void;
}

export const useGoogleCalendarStore = create<GoogleCalendarState>((set, get) => ({
  events: [],
  loading: false,
  error: null,
  configured: true,
  collegato: true,
  accountGoogle: null,
  puoiCollegare: false,
  lastRange: null,

  fetchEvents: async (timeMin, timeMax) => {
    set({ loading: true, error: null });

    // Le azioni lead vanno mostrate SEMPRE, anche se Google non è configurato/errore.
    const leadItems = await fetchLeadAgenda(timeMin, timeMax);
    const lastRange = { min: timeMin.getTime(), max: timeMax.getTime() };

    try {
      const { data, error } = await supabase.functions.invoke('google-calendar-events', {
        body: { timeMin: timeMin.toISOString(), timeMax: timeMax.toISOString() },
      });

      if (error) {
        // Su risposta non-2xx supabase-js mette il body nella Response error.context:
        // estraiamo il messaggio dettagliato della function (es. "Google token error (400): ...").
        let detail = error.message;
        const ctx = (error as { context?: Response }).context;
        if (ctx && typeof ctx.text === 'function') {
          try {
            const raw = await ctx.text();
            const parsed = JSON.parse(raw);
            if (parsed?.error) detail = parsed.error;
          } catch {
            /* body non JSON: teniamo error.message */
          }
        }
        throw new Error(detail);
      }

      if (data && data.ok === false) {
        if (data.error === 'google_not_configured') {
          set({ events: leadItems, configured: false, loading: false, error: null, lastRange });
          return;
        }
        // Non è un errore: il calendario aziendale non è ancora collegato, o
        // il permesso è stato revocato dall'account Google dell'azienda.
        if (data.error === 'google_non_collegato') {
          set({
            events: leadItems,
            configured: true,
            collegato: false,
            accountGoogle: null,
            loading: false,
            error: null,
            lastRange,
          });
          return;
        }
        throw new Error(data.error || 'Errore Google Calendar');
      }

      const gEvents = ((data?.events ?? []) as RawEvent[]).map(rawToEvent);
      set({
        events: [...gEvents, ...leadItems],
        configured: true,
        collegato: true,
        loading: false,
        lastRange,
      });
    } catch (err) {
      console.error('Error fetching Google Calendar events:', err);
      // Google fallito: mostra comunque le azioni lead.
      set({
        events: leadItems,
        error: err instanceof Error ? err.message : 'Errore nel caricamento del Google Calendar',
        loading: false,
        lastRange,
      });
    }
  },

  leggiStatoGoogle: async () => {
    const { data, error } = await supabase.functions.invoke('google-oauth', {
      body: { azione: 'stato' },
    });
    if (error || !data?.ok) return;
    set({
      configured: !!data.configurato,
      collegato: !!data.collegato,
      accountGoogle: data.email ?? null,
      puoiCollegare: !!data.puoiCollegare,
    });
  },

  collegaGoogle: async () => {
    set({ error: null });
    const { data, error } = await supabase.functions.invoke('google-oauth', {
      body: { azione: 'inizia' },
    });

    if (error || !data?.ok || !data.url) {
      set({
        error:
          data?.errore === 'google_non_configurato'
            ? "L'applicazione non è ancora registrata su Google: mancano le credenziali sul server."
            : error?.message || data?.errore || 'Impossibile avviare il collegamento con Google.',
      });
      return;
    }

    // Si esce da Pixel e si va su Google: al ritorno la pagina si ricarica da
    // sola sull'indirizzo del Calendario.
    window.location.href = data.url as string;
  },

  scollegaGoogle: async () => {
    const { data, error } = await supabase.functions.invoke('google-oauth', {
      body: { azione: 'scollega' },
    });
    if (error || !data?.ok) {
      set({ error: error?.message || data?.errore || 'Impossibile scollegare Google.' });
      return;
    }
    // Restano solo le scadenze dei lead, che non vengono da Google: le voci
    // di Google spariscono subito, senza aspettare il prossimo caricamento.
    // Sparirebbero comunque a tutti: il calendario collegato è uno solo.
    set({
      collegato: false,
      accountGoogle: null,
      events: get().events.filter((e) => e.id.startsWith('lead:')),
    });
  },

  getEventsByDate: (date) => {
    const dateStart = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
    const dateEnd = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59);

    return get().events
      .filter((ev) => {
        const start = new Date(ev.startDate);
        return start >= dateStart && start <= dateEnd;
      })
      .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
  },

  getTodayEvents: () => get().getEventsByDate(new Date()),

  clearError: () => set({ error: null }),
}));
